import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MESSAGES } from "@/constants";
import { showErrorToast } from "@/helpers/toast.helpers";
import { slackUserLinkQueryKeyPrefix } from "@/hooks/useSlackUserLink";
import {
  getSlackStatus,
  getSlackInstallUrl,
  getSlackShareInstallUrl,
  disconnectSlack,
  SlackAppKey,
  SlackStatusResponse,
  SlackShareInstallUrlResponse,
} from "@/services/apis/slackApi";

/** Per-app status query key — one cache entry per Slack app. */
export const slackStatusQueryKey = (appKey: SlackAppKey) => ["slack", appKey, "status"] as const;

// Module-level cache shared across all useSlack instances so every entry
// point (NavMain, SlackBanner, Integration) sees the same link. Keyed by app so
// the IQ link and the IQ Data Engineer link never overwrite each other.
const shareInstallCache = new Map<SlackAppKey, { url: string; expiresAt: string }>();

// Safety buffer — treat the link as expired 60s before actual expiry
// so users don't copy/share a link that dies moments later.
const SHARE_LINK_EXPIRY_BUFFER_MS = 60_000;

function isShareCacheValid(appKey: SlackAppKey): boolean {
  const cached = shareInstallCache.get(appKey);
  if (!cached) return false;
  const expiresAt = new Date(cached.expiresAt).getTime();
  return Date.now() < expiresAt - SHARE_LINK_EXPIRY_BUFFER_MS;
}

/** Reset module-level cache (every app). Used in test setup and logout flows to
 *  prevent stale links from leaking across sessions. */
export function resetShareInstallCache() {
  shareInstallCache.clear();
}

export function useSlack(appKey: SlackAppKey = "iq") {
  const queryClient = useQueryClient();
  const statusQueryKey = slackStatusQueryKey(appKey);

  const {
    data: slackStatus,
    isLoading: isLoadingStatus,
    isError: isStatusError,
    refetch: refetchStatus,
  } = useQuery<SlackStatusResponse>({
    queryKey: statusQueryKey,
    queryFn: () => getSlackStatus(appKey),
    retry: false,
    refetchOnWindowFocus: false,
    // Every mounting consumer (NavMain, SlackBanner, Integration) otherwise refires
    // the status request — with the backend down that's a wall of console errors.
    // `staleTime` only dedupes the success path (a cached-fresh result is reused);
    // an errored query has no fresh data, so `retryOnMount: false` is what stops a
    // later-mounting consumer from refiring during an outage.
    staleTime: 60_000,
    retryOnMount: false,
  });

  /*
  * 'close' event is handled on the IQ page when Slack redirects back to IQ
  * No need to handle the event here
  */
  const connectMutation = useMutation({
    mutationFn: () => getSlackInstallUrl(appKey),
    onSuccess: (data) => {
      window.open(data.url, "_blank", "noopener");
    },
    onError: () => {
      showErrorToast(MESSAGES.SLACK.CONNECT_DIALOG.LINK_FETCH_FAILED);
    },
  });

  const shareInstallMutation = useMutation({
    mutationFn: () => getSlackShareInstallUrl(appKey),
  });
  // Pulled out because the mutation object is a new reference every render —
  // only `mutateAsync` is stable enough to sit in a dependency array.
  const { mutateAsync: fetchShareInstallUrl } = shareInstallMutation;

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectSlack(appKey),
    onSuccess: () => {
      shareInstallCache.delete(appKey);
      queryClient.setQueryData(statusQueryKey, (old: SlackStatusResponse | undefined) => ({
        ...(old ?? {}),
        connected: false,
        team_id: null,
        team_name: null,
        installed_at: null,
        company_id: null,
      }));
      // The personal link row carries its own `workspace_connected`; without
      // this it keeps saying "ask IQ in Slack" under a disconnected card. By
      // prefix: the install is the company's, so every cached person is stale.
      if (appKey === "iq") {
        queryClient.invalidateQueries({ queryKey: slackUserLinkQueryKeyPrefix });
      }
    },
  });

  const connectSlack = () => {
    connectMutation.mutate();
  };

  // Memoised on purpose: SlackConnectDialog auto-fetches the link from an effect
  // keyed off this function, so a fresh identity every render re-fires that
  // effect and hammers the endpoint.
  const shareInstallLink = useCallback(async (): Promise<SlackShareInstallUrlResponse> => {
    if (isShareCacheValid(appKey)) {
      const cached = shareInstallCache.get(appKey)!;
      return { url: cached.url, expires_at: cached.expiresAt };
    }
    const data = await fetchShareInstallUrl();
    shareInstallCache.set(appKey, { url: data.url, expiresAt: data.expires_at });
    return data;
  }, [appKey, fetchShareInstallUrl]);

  const disconnectSlackWorkspace = () => {
    return disconnectMutation.mutateAsync();
  };

  return {
    isConnected: slackStatus?.connected ?? false,
    teamName: slackStatus?.team_name ?? null,
    slackStatus,
    isLoadingStatus,
    isStatusError,
    refetchStatus,
    connectSlack,
    isConnecting: connectMutation.isPending,
    connectError: connectMutation.error,
    shareInstallLink,
    isShareLoading: shareInstallMutation.isPending,
    shareSuccess: shareInstallMutation.isSuccess,
    disconnectSlack: disconnectSlackWorkspace,
    isDisconnecting: disconnectMutation.isPending,
    disconnectError: disconnectMutation.error,
    disconnectSuccess: disconnectMutation.isSuccess,
  };
}
